/**
 * Inspection scheduling and reminder maths.
 *
 * Shared by the API and the app so a due date is never computed two different
 * ways. All dates crossing this boundary are plain 'YYYY-MM-DD' strings in
 * local (UK) terms - no timezone drift on a date that legally matters.
 */

export type DateStr = string; // YYYY-MM-DD

/** Standard PMI frequencies offered in the UI. */
export const INTERVAL_WEEKS = [4, 6, 8, 10, 12, 13] as const;

/** DVSA minimum retention for safety inspection and repair records. */
export const RETENTION_MONTHS = 15;

export function toDateStr(d: Date): DateStr {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDate(s: DateStr): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function today(): DateStr {
  return toDateStr(new Date());
}

export function addDays(s: DateStr, days: number): DateStr {
  const d = parseDate(s);
  d.setDate(d.getDate() + days);
  return toDateStr(d);
}

export function addWeeks(s: DateStr, weeks: number): DateStr {
  return addDays(s, weeks * 7);
}

export function addMonths(s: DateStr, months: number): DateStr {
  const d = parseDate(s);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  // Clamp when the target month is shorter (31 Jan + 1 month -> 28/29 Feb).
  if (d.getDate() < day) d.setDate(0);
  return toDateStr(d);
}

/** Whole days from `from` to `to`. Negative when `to` is in the past. */
export function daysBetween(from: DateStr, to: DateStr): number {
  const ms = parseDate(to).getTime() - parseDate(from).getTime();
  return Math.round(ms / 86_400_000);
}

/* ------------------------------------------------------------------ *
 * ISO week
 *
 * The RHA form records an "ISO Week Number", and the Notes require the
 * inspection to fall inside the agreed weekly interval, so the week is
 * part of the compliance record rather than a display nicety.
 * ------------------------------------------------------------------ */

export function isoWeek(s: DateStr): { year: number; week: number } {
  const d = parseDate(s);
  // Shift to the Thursday of this week; the year of that Thursday is the ISO year.
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayNum = (target.getDay() + 6) % 7; // Mon = 0
  target.setDate(target.getDate() - dayNum + 3);
  const isoYear = target.getFullYear();
  const firstThursday = new Date(isoYear, 0, 4);
  const firstDayNum = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNum + 3);
  const week = 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
  return { year: isoYear, week };
}

export function formatIsoWeek(s: DateStr): string {
  const { year, week } = isoWeek(s);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/* ------------------------------------------------------------------ *
 * Due dates and reminder state
 * ------------------------------------------------------------------ */

export function nextInspectionDue(last: DateStr | null, intervalWeeks: number): DateStr | null {
  if (!last) return null;
  return addWeeks(last, intervalWeeks);
}

export type ReminderState = 'overdue' | 'due' | 'soon' | 'ok' | 'unknown';

export interface ReminderStatus {
  state: ReminderState;
  /** Days until due. Negative when overdue. */
  days: number | null;
  due: DateStr | null;
  label: string;
}

/**
 * Warning windows. Inspections get a tighter window than annual dates because
 * the workshop needs to book the vehicle in, not just be aware of it.
 */
const WINDOWS = {
  inspection: { due: 7, soon: 21 },
  annual: { due: 30, soon: 60 },
} as const;

export type ReminderKind = keyof typeof WINDOWS;

export function reminderStatus(
  due: DateStr | null,
  kind: ReminderKind = 'inspection',
  from: DateStr = today(),
): ReminderStatus {
  if (!due) return { state: 'unknown', days: null, due: null, label: 'Not set' };
  const days = daysBetween(from, due);
  const w = WINDOWS[kind];
  let state: ReminderState;
  if (days < 0) state = 'overdue';
  else if (days <= w.due) state = 'due';
  else if (days <= w.soon) state = 'soon';
  else state = 'ok';
  return { state, days, due, label: describeDays(days) };
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/**
 * A relative span in the largest unit that still reads naturally.
 *
 * The exact date is always shown alongside this, so precision matters less
 * than legibility: a tachograph calibration is two-yearly and an O-Licence
 * runs five, and "In 24 months" or "731 days overdue" are worse than the
 * years they describe.
 */
function describeSpan(days: number): string {
  if (days < 14) return plural(days, 'day');
  if (days < 60) return plural(Math.round(days / 7), 'week');
  if (days < 365) return plural(Math.round(days / 30), 'month');
  return plural(Math.round(days / 365), 'year');
}

function describeDays(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  // Deliberately not "Yesterday": on a compliance list the word that has to
  // register is "overdue".
  if (days < 0) return `${describeSpan(Math.abs(days))} overdue`;
  return `In ${describeSpan(days)}`;
}

/** Ordering for the dashboard: worst first, then soonest. */
export const STATE_RANK: Record<ReminderState, number> = {
  overdue: 0,
  due: 1,
  soon: 2,
  ok: 3,
  unknown: 4,
};

export interface AssetDates {
  next_inspection_date?: DateStr | null;
  mot_expiry?: DateStr | null;
  tacho_calibration_expiry?: DateStr | null;
  plating_expiry?: DateStr | null;
  tax_expiry?: DateStr | null;
  insurance_expiry?: DateStr | null;
  loler_expiry?: DateStr | null;
}

export interface DueEntry {
  key: keyof AssetDates;
  title: string;
  kind: ReminderKind;
  status: ReminderStatus;
}

const DUE_FIELDS: { key: keyof AssetDates; title: string; kind: ReminderKind }[] = [
  { key: 'next_inspection_date', title: 'Safety inspection', kind: 'inspection' },
  { key: 'mot_expiry', title: 'MOT / annual test', kind: 'annual' },
  { key: 'tacho_calibration_expiry', title: 'Tachograph calibration', kind: 'annual' },
  { key: 'plating_expiry', title: 'Plating certificate', kind: 'annual' },
  { key: 'loler_expiry', title: 'LOLER (tail lift / crane)', kind: 'annual' },
  { key: 'tax_expiry', title: 'Vehicle tax', kind: 'annual' },
  { key: 'insurance_expiry', title: 'Insurance', kind: 'annual' },
];

/** Every dated obligation on an asset, worst first. */
export function assetDueList(asset: AssetDates, from: DateStr = today()): DueEntry[] {
  return DUE_FIELDS.map((f) => ({
    ...f,
    status: reminderStatus(asset[f.key] ?? null, f.kind, from),
  })).sort((a, b) => {
    const rank = STATE_RANK[a.status.state] - STATE_RANK[b.status.state];
    return rank !== 0 ? rank : (a.status.days ?? 1e9) - (b.status.days ?? 1e9);
  });
}

/** The single worst obligation on an asset, for list rows and badges. */
export function worstStatus(asset: AssetDates, from: DateStr = today()): DueEntry | null {
  const list = assetDueList(asset, from).filter((e) => e.status.state !== 'unknown');
  return list[0] ?? null;
}

/**
 * Forward PMI plan - the schedule DVSA asks to see at audit.
 * Projects from the last completed inspection, or from today if there is none.
 */
export function forwardPlan(
  last: DateStr | null,
  intervalWeeks: number,
  count = 6,
  from: DateStr = today(),
): DateStr[] {
  let cursor = last ?? from;
  const dates: DateStr[] = [];
  for (let i = 0; i < count; i++) {
    cursor = addWeeks(cursor, intervalWeeks);
    dates.push(cursor);
  }
  return dates;
}

/** Date before which records may be discarded. Kept deliberately conservative. */
export function retentionCutoff(from: DateStr = today()): DateStr {
  return addMonths(from, -RETENTION_MONTHS);
}

/**
 * The Notes on the form require vehicles 12 years and older to be inspected at
 * no more than 6-weekly intervals. Returns a warning when the configured
 * interval breaches that, or null when it is fine.
 */
export function intervalWarning(
  firstUseDate: DateStr | null | undefined,
  intervalWeeks: number,
  from: DateStr = today(),
): string | null {
  if (!firstUseDate) return null;
  const years = daysBetween(firstUseDate, from) / 365.25;
  if (years >= 12 && intervalWeeks > 6) {
    return `This ${Math.floor(years)}-year-old vehicle is set to ${intervalWeeks}-weekly. Vehicles 12 years and older are expected to be inspected at no more than 6-weekly intervals.`;
  }
  return null;
}

export function formatDate(s: DateStr | null | undefined): string {
  if (!s) return '—';
  const d = parseDate(s);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
